// Engine ring — operator surface for the per-instrument matching peers
// sharded by consistent hash.
//
// Wire format mirrors engine-router/internal/adapter/http/server.go
// (peerDTO / leaseDTO / statusDTO). Health is the tri-state the router
// publishes (HEALTHY / UNHEALTHY / UNKNOWN); a peer is *routable* iff
// health=HEALTHY AND draining=false — surfaced as `healthy: bool` and
// `in_ring: bool` so we don't re-derive them client-side. Lease.state
// distinguishes ACTIVE vs MIGRATING — during a migration we render the
// destination peer next to the source so the operator can watch the
// in-flight ring change.
//
// The status panel reads /admin/engine/status to seed the Scale input
// with the current desired_replicas and surface kube_mode so the
// operator knows whether actions actually reach Kubernetes. The Scale
// request body now carries only `replicas` — the engine-router resolves
// namespace + statefulset from its boot config.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchEngineStatus,
  fetchPeers,
  fetchOwnership,
  scaleEngines,
  rebalanceEngines,
  drainPeer,
  undrainPeer,
  restartPeer,
  triggerEngineSync,
  keys,
} from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { NumberInput } from "@/components/ui/NumberInput";
import { EmptyState } from "@/components/ui/EmptyState";
import { fmtRelative } from "@/lib/format";
import type {
  LeaseState,
  PeerInfo,
  OwnershipLease,
  RebalanceResult,
} from "@/lib/types";

export const Route = createFileRoute("/_app/engine")({
  component: EnginePage,
});

function leaseTone(s: LeaseState | "IDLE"): "success" | "warning" | "muted" {
  switch (s) {
    case "ACTIVE":    return "success";
    case "MIGRATING": return "warning";
    case "IDLE":      return "muted";
    default:          return "muted";
  }
}

// extractError pulls the message off either an APIError-shaped throw or a
// plain Error. Used so the error banner doesn't render "[object Object]".
function extractError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof Error) return err.message;
  return String(err);
}

function MetricTile({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "success" | "warning" | "danger";
}) {
  const toneClass =
    tone === "success" ? "text-success"
    : tone === "warning" ? "text-warning"
    : tone === "danger" ? "text-danger"
    : "text-t-1";
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line-1 bg-bg-2/40 px-4 py-3">
      <span className="text-2xs font-semibold uppercase tracking-wide text-t-3">{label}</span>
      <span className={`font-display text-xl tracking-tight ${toneClass}`}>{value}</span>
      {hint && <span className="text-2xs text-t-3">{hint}</span>}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-no/40 bg-no/10 px-3 py-2 text-2xs text-no">
      {message}
    </div>
  );
}

function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-yes/40 bg-yes/10 px-3 py-2 text-2xs text-yes">
      {message}
    </div>
  );
}

// peerIsRecovering returns true when a recent Restart click is still in
// flight for this peer (pod deletion → SIGTERM → new pod → ring rejoin
// → markets re-registered). Used to swap the alarming "rejected /
// drained" copy for a passive "restoring" indicator.
function peerIsRecovering(peerID: string, tracking: RestartTracking | null) {
  return tracking !== null && tracking.peerID === peerID && tracking.completedAt === undefined;
}

// RingDownAlert fires when the router has zero routable peers. In that
// state every POST /orders returns sync REJECTED. Copy branches three
// ways: an admin-drained singleton (offer inline Undrain), a known
// in-flight restart (passive "restoring" copy with no action — the
// recovery is automatic), or generic "investigate" guidance when neither
// applies. Without the restart-aware branch the banner falsely accuses
// the operator of a manual drain immediately after they clicked Restart,
// which is the exact bad-UX path that motivated this rewrite.
function RingDownAlert({
  peers,
  onUndrain,
  undrainingPeer,
  restartTracking,
}: {
  peers: PeerInfo[];
  onUndrain: (peerID: string) => void;
  undrainingPeer: string | undefined;
  restartTracking: RestartTracking | null;
}) {
  const recovering =
    restartTracking !== null && restartTracking.completedAt === undefined;
  const drainedPeers = peers.filter((p) => p.operator_drained);
  // While a restart is in flight, exclude the restarting peer from the
  // "admin-drained singleton" check — its operator_drained=true is a
  // SIGTERM artifact, not an operator action, and we don't want to
  // offer an Undrain button for state that will clear itself.
  const drainedExcludingRestart = recovering
    ? drainedPeers.filter((p) => p.peer_id !== restartTracking!.peerID)
    : drainedPeers;
  const drainedSingleton =
    drainedExcludingRestart.length === 1 ? drainedExcludingRestart[0] : null;

  if (recovering) {
    const ageSec = Math.floor((Date.now() - restartTracking!.startedAt) / 1000);
    return (
      <div className="rounded-md border border-warning bg-warning/15 px-4 py-3 text-sm text-warning">
        <div className="font-semibold">
          Restoring <span className="font-mono">{restartTracking!.peerID}</span>…
        </div>
        <div className="mt-1 text-xs text-warning/80">
          Pod is being recreated by the StatefulSet. Order placement will
          recover automatically once the new pod announces, rejoins the
          ring, and the market catalog re-syncs (typically 10–40 s).
          Elapsed: {ageSec}s.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-no bg-no/15 px-4 py-3 text-sm text-no">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="font-semibold">
            No routable engine peers — order placements are being rejected
          </div>
          <div className="mt-1 text-xs text-no/80">
            The matching ring is empty. Every order placement returns sync
            REJECTED until at least one peer is HEALTHY and in the ring.{" "}
            {drainedSingleton ? (
              <>
                Peer{" "}
                <span className="font-mono text-no">
                  {drainedSingleton.peer_id}
                </span>{" "}
                is admin-drained — undrain it to restore routing.
              </>
            ) : drainedExcludingRestart.length > 1 ? (
              <>Undrain a drained peer below, or scale the StatefulSet up.</>
            ) : (
              <>
                No peers are admin-drained. Try restarting a stuck peer
                below, scale the StatefulSet up, or investigate why
                announced peers are UNHEALTHY.
              </>
            )}
          </div>
        </div>
        {drainedSingleton && (
          <Button
            size="sm"
            onClick={() => onUndrain(drainedSingleton.peer_id)}
            disabled={undrainingPeer === drainedSingleton.peer_id}
          >
            {undrainingPeer === drainedSingleton.peer_id
              ? "Undraining…"
              : `Undrain ${drainedSingleton.peer_id}`}
          </Button>
        )}
      </div>
    </div>
  );
}

// RestartTracking captures a recent Restart click so the page can:
//   - poll faster (1 s vs 5 s) until the peer is fully recovered, so the
//     "Restoring…" indicator updates near-real-time;
//   - swap banner + per-row badge copy to reflect the in-flight restart
//     instead of falsely surfacing it as a stuck operator-drain;
//   - hide the per-row Undrain CTA so the operator doesn't fight the
//     automatic recovery.
//
// completedAt is set once the peer reports in_ring + healthy + has
// markets, at which point fast-polling stops but the success badge
// lingers briefly so the operator sees confirmation.
type RestartTracking = {
  peerID: string;
  startedAt: number;
  completedAt?: number;
  // syncTriggered=true once we've fired triggerEngineSync for this
  // restart cycle — guards against re-firing on every poll once the
  // peer enters the ring but hasn't yet reported instruments.
  syncTriggered?: boolean;
};

// peerStatusBadge collapses the per-row health/drain/ring badges into a
// single routability-tone badge so the operator never sees a green
// HEALTHY tag next to a yellow DRAINING — the previous shape was
// technically correct but visually misleading ("HEALTHY" reads as "you
// can place orders" even when the peer is held out of the ring).
function peerStatusBadge(p: PeerInfo, recovering: boolean) {
  if (recovering) return { tone: "warning" as const, label: "restarting" };
  if (p.health === "UNHEALTHY") return { tone: "danger" as const, label: "unhealthy" };
  if (p.health === "UNKNOWN") return { tone: "muted" as const, label: "unknown" };
  if (p.in_ring && p.healthy) return { tone: "success" as const, label: "routable" };
  if (p.operator_drained) return { tone: "danger" as const, label: "admin-drained" };
  if (p.draining) return { tone: "warning" as const, label: "engine draining" };
  return { tone: "danger" as const, label: "out of ring" };
}

function EnginePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // Tracks a recent Restart click so the page can fast-poll until the
  // pod is back in the ring + markets re-registered, swap the banner
  // copy to a passive "restoring" indicator, and skip surfacing the
  // SIGTERM-set operator_drained flag as an actionable problem.
  // Cleared on full recovery (with a brief success grace period) or on
  // a 90 s timeout safety net.
  const [restartTracking, setRestartTracking] = useState<RestartTracking | null>(null);

  // Fast cadence while a restart is in flight so the operator sees the
  // recovery as it happens; default 5 s otherwise to keep idle load
  // light. Ownership stays at 10 s either way — lease churn during a
  // restart is mostly already covered by the peers refresh.
  const fastPollMs = restartTracking !== null ? 1_000 : 5_000;

  const status = useQuery({
    queryKey: keys.status(),
    queryFn: fetchEngineStatus,
    refetchInterval: fastPollMs,
  });
  const peers = useQuery({
    queryKey: keys.peers(),
    queryFn: fetchPeers,
    refetchInterval: fastPollMs,
  });
  const ownership = useQuery({
    queryKey: keys.ownership(),
    queryFn: fetchOwnership,
    refetchInterval: 10_000,
  });

  // Selected shard — clicking a peer in the table filters the lease list
  // and the instruments-by-peer panel below. Default null = no filter.
  const [selectedPeer, setSelectedPeer] = useState<string | null>(null);

  // Replicas input. Initialised to "" so we know whether to seed from
  // the status response — when the user types we stop tracking
  // server-side changes (otherwise the input would jump out from
  // under them every 5s).
  const [replicas, setReplicas] = useState("");
  const [replicasTouched, setReplicasTouched] = useState(false);

  // Seed the input once status arrives, and keep it in sync with the
  // server until the operator types — at which point we yield control.
  useEffect(() => {
    if (replicasTouched) return;
    const desired = status.data?.desired_replicas;
    const fallback = status.data?.actual_peers ?? peers.data?.length;
    const seed = desired ?? fallback;
    if (seed !== undefined && seed !== null) {
      setReplicas(String(seed));
    }
  }, [status.data, peers.data, replicasTouched]);

  const handleReplicasChange = (v: string) => {
    setReplicasTouched(true);
    setReplicas(v);
  };

  const scale = useMutation({
    mutationFn: () => scaleEngines(parseInt(replicas, 10) || 0),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.engine });
      // Re-arm the auto-seed so the input reflects the new desired count
      // on the next status refresh.
      setReplicasTouched(false);
    },
    meta: { successMessage: "Engine replicas updated." },
  });
  const rebalance = useMutation<RebalanceResult>({
    mutationFn: () => rebalanceEngines(),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engine }),
    meta: { successMessage: "Rebalance triggered." },
  });
  const drain = useMutation({
    mutationFn: (peerID: string) => drainPeer(peerID),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engine }),
    meta: { successMessage: "Peer draining." },
  });
  const undrain = useMutation({
    mutationFn: (peerID: string) => undrainPeer(peerID),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engine }),
    meta: { successMessage: "Peer returned to service." },
  });
  const restart = useMutation({
    mutationFn: (peerID: string) => restartPeer(peerID),
    onSuccess: (_, peerID) => {
      // Seed the tracker so banner + per-row state immediately shift to
      // "Restoring…" mode. The recovery-watch effect below clears it
      // when the peer rejoins the ring with markets registered.
      setRestartTracking({ peerID, startedAt: Date.now() });
      qc.invalidateQueries({ queryKey: keys.engine });
    },
    meta: { successMessage: "Peer restart triggered." },
  });

  // Peers sorted deterministically so re-renders don't shuffle the list
  // (chi map iteration is unordered upstream).
  const sortedPeers = useMemo<PeerInfo[]>(() => {
    return (peers.data ?? []).slice().sort((a, b) =>
      a.peer_id.localeCompare(b.peer_id),
    );
  }, [peers.data]);

  // When a peer is removed (e.g. scale-down), drop the selection so we
  // don't render a phantom filter the operator can't clear by clicking.
  useEffect(() => {
    if (!selectedPeer) return;
    if (sortedPeers.some((p) => p.peer_id === selectedPeer)) return;
    setSelectedPeer(null);
  }, [sortedPeers, selectedPeer]);

  // Recovery watcher: drives two transitions during a restart cycle:
  //
  //   1. Peer enters the ring but reports zero instruments → fire
  //      triggerEngineSync once. This pokes market-service to re-issue
  //      OpenMarket for every published market immediately, collapsing
  //      the otherwise-30-s wait for the next engine-syncer tick. We
  //      fire only when in_ring=true to avoid hitting market-service
  //      before the engine can actually accept OpenMarket commands;
  //      and we set syncTriggered=true so the next poll doesn't re-fire
  //      the trigger every second until instruments populate.
  //
  //   2. Peer is fully routable AND has instruments → recovery
  //      complete. Mark completedAt; the success-window effect below
  //      keeps the green badge visible briefly before dropping
  //      fast-poll.
  //
  // 90 s timeout cap exists for the pathological case where the pod
  // never returns to a usable state — better to stop hammering than
  // spin forever.
  useEffect(() => {
    if (!restartTracking || restartTracking.completedAt !== undefined) return;
    const peer = sortedPeers.find(
      (p) => p.peer_id === restartTracking.peerID,
    );
    const inRing = peer?.in_ring && peer.healthy;
    const hasInstruments = (peer?.instruments?.length ?? 0) > 0;

    if (inRing && hasInstruments) {
      setRestartTracking({ ...restartTracking, completedAt: Date.now() });
      return;
    }
    if (inRing && !hasInstruments && !restartTracking.syncTriggered) {
      // Mark as triggered first to prevent re-fires; the API call
      // itself is fire-and-forget (idempotent — if it fails the next
      // engine-syncer tick will still catch up within 30 s).
      setRestartTracking({ ...restartTracking, syncTriggered: true });
      triggerEngineSync().catch(() => {
        // Swallow — the periodic syncer is the safety net.
      });
      return;
    }
    if (Date.now() - restartTracking.startedAt > 90_000) {
      setRestartTracking(null);
    }
  }, [sortedPeers, restartTracking]);

  // Drop the completed marker after the success window so badges + poll
  // cadence return to steady state.
  useEffect(() => {
    if (!restartTracking?.completedAt) return;
    const t = setTimeout(() => setRestartTracking(null), 5_000);
    return () => clearTimeout(t);
  }, [restartTracking]);

  const selectedPeerInfo = useMemo<PeerInfo | null>(() => {
    if (!selectedPeer) return null;
    return sortedPeers.find((p) => p.peer_id === selectedPeer) ?? null;
  }, [sortedPeers, selectedPeer]);

  // Instrument rows for the right-hand table. Two distinct views:
  //
  //  - No peer selected: show every active ownership lease in the
  //    router (the lease store is the routing-time source of truth).
  //
  //  - Peer selected: show the union of (a) leases routed to that
  //    peer and (b) the instruments the peer reports in its
  //    heartbeat. Engine-side workers are durable per-instrument
  //    until migration releases them, but the router's leases expire
  //    after OWNERSHIP_LEASE_TTL_MS (10s default) if no command
  //    re-claims them — so the heartbeat list is the authoritative
  //    "what this shard handles" set, while leases tell you which
  //    of those are actively being routed *right now*. Without the
  //    union the table would mislead the operator into thinking the
  //    shard dropped ownership of a quiet instrument when in fact
  //    the engine still holds its worker and book.
  type InstrumentRow = {
    market_id: string;
    peer_id: string;
    state: LeaseState | "IDLE";
    expires_at?: string;
    migrating_to?: string;
  };
  const instrumentRows = useMemo<InstrumentRow[]>(() => {
    const leases = ownership.data ?? [];
    if (!selectedPeer) {
      return leases.map((l) => ({
        market_id: l.market_id,
        peer_id: l.peer_id,
        state: l.state,
        expires_at: l.expires_at,
        migrating_to: l.migrating_to,
      }));
    }
    // Map of market_id → lease for the selected peer (covers both
    // current-owner and migration-target roles).
    const byInstrument = new Map<string, OwnershipLease>();
    for (const l of leases) {
      if (l.peer_id === selectedPeer || l.migrating_to === selectedPeer) {
        byInstrument.set(l.market_id, l);
      }
    }
    const heartbeat = selectedPeerInfo?.instruments ?? [];
    const seen = new Set<string>();
    const out: InstrumentRow[] = [];
    // 1. Heartbeat-reported instruments first, joined with lease state.
    for (const id of heartbeat) {
      seen.add(id);
      const l = byInstrument.get(id);
      out.push(
        l
          ? {
              market_id: id,
              peer_id: l.peer_id,
              state: l.state,
              expires_at: l.expires_at,
              migrating_to: l.migrating_to,
            }
          : { market_id: id, peer_id: selectedPeer, state: "IDLE" },
      );
    }
    // 2. Leases that exist for this peer but aren't in the heartbeat
    //    yet (newly-claimed instrument before next 2s heartbeat).
    for (const l of byInstrument.values()) {
      if (seen.has(l.market_id)) continue;
      out.push({
        market_id: l.market_id,
        peer_id: l.peer_id,
        state: l.state,
        expires_at: l.expires_at,
        migrating_to: l.migrating_to,
      });
    }
    out.sort((a, b) => a.market_id.localeCompare(b.market_id));
    return out;
  }, [ownership.data, selectedPeer, selectedPeerInfo]);

  const totalInstrumentsForPeer = useMemo(() => {
    if (!selectedPeer) return ownership.data?.length ?? 0;
    const set = new Set<string>(selectedPeerInfo?.instruments ?? []);
    for (const l of ownership.data ?? []) {
      if (l.peer_id === selectedPeer || l.migrating_to === selectedPeer) {
        set.add(l.market_id);
      }
    }
    return set.size;
  }, [ownership.data, selectedPeer, selectedPeerInfo]);
  const activeLeaseCount = instrumentRows.filter(
    (row) => row.state === "ACTIVE" || row.state === "MIGRATING",
  ).length;

  const isMockMode = status.data?.kube_mode === "mock";
  const replicasNum = parseInt(replicas, 10);
  const replicasInvalid = !Number.isFinite(replicasNum) || replicasNum < 0;
  // Ring is "down" when the router knows about peers (status loaded)
  // but none are routable. Suppressed during initial load + on error so
  // we don't flash a false alarm before the first /admin/engine/status
  // response lands.
  const ringDown =
    status.data !== undefined && status.data.healthy_peers === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Engine ring</h1>
        <p className="text-sm text-t-3">
          Per-instrument matching peers sharded by consistent hash. Scale via
          the StatefulSet replica count; drain a peer to stop new routing;
          rebalance to migrate ownership to the desired peer per the ring.
        </p>
      </div>

      {ringDown && (
        <RingDownAlert
          peers={sortedPeers}
          onUndrain={(id) => undrain.mutate(id)}
          undrainingPeer={
            undrain.isPending ? (undrain.variables as string) : undefined
          }
          restartTracking={restartTracking}
        />
      )}

      {/* --- Status overview --- */}
      <Card>
        <CardHeader>
          <CardTitle>Cluster status</CardTitle>
          <span className="text-2xs text-t-3">
            {status.isLoading
              ? "loading…"
              : status.data
                ? `${status.data.namespace}/${status.data.statefulset} · ${status.data.kube_mode}`
                : "—"}
          </span>
        </CardHeader>
        <CardBody className="space-y-3">
          {status.isError && (
            <ErrorBanner
              message={`Status unavailable: ${extractError(status.error) ?? "unknown error"}`}
            />
          )}
          {isMockMode && (
            <ErrorBanner message="Engine-router is in mock mode — Scale will not reach a real Kubernetes StatefulSet. Set KUBE_IN_CLUSTER=true to enable real scaling." />
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            <MetricTile
              label="Desired replicas"
              value={status.data?.desired_replicas ?? "—"}
              hint="StatefulSet.spec.replicas"
            />
            <MetricTile
              label="Actual peers"
              value={status.data?.actual_peers ?? sortedPeers.length}
              hint="discovered by router"
            />
            <MetricTile
              label="Healthy"
              value={status.data?.healthy_peers ?? sortedPeers.filter((p) => p.healthy).length}
              hint="routable now"
              tone="success"
            />
            <MetricTile
              label="Draining"
              value={status.data?.draining_peers ?? sortedPeers.filter((p) => p.draining).length}
              hint="held out of ring"
              tone={
                (status.data?.draining_peers ?? 0) > 0 ? "warning" : "default"
              }
            />
            <MetricTile
              label="Ring members"
              value={status.data?.ring_size ?? "—"}
              hint="consistent-hash"
            />
            <MetricTile
              label="Owned instruments"
              value={
                status.data?.owned_instruments ?? ownership.data?.length ?? "—"
              }
              hint="active leases"
            />
          </div>
        </CardBody>
      </Card>

      {/* --- Operator actions --- */}
      <Card>
        <CardHeader>
          <CardTitle>Operator actions</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32">
              <NumberInput
                label="Replicas"
                value={replicas}
                onChange={handleReplicasChange}
                hint={
                  status.data
                    ? `current: ${status.data.desired_replicas}`
                    : "current: —"
                }
              />
            </div>
            <Button
              onClick={() => scale.mutate()}
              loading={scale.isPending}
              disabled={replicasInvalid || isMockMode}
              title={
                isMockMode
                  ? "Disabled in mock mode"
                  : replicasInvalid
                    ? "Enter a non-negative integer"
                    : undefined
              }
            >
              Scale
            </Button>
            <Button
              variant="secondary"
              onClick={() => rebalance.mutate()}
              loading={rebalance.isPending}
            >
              Rebalance ring
            </Button>
          </div>

          {scale.isError && (
            <ErrorBanner
              message={`Scale failed: ${extractError(scale.error) ?? "unknown error"}`}
            />
          )}
          {scale.isSuccess && (
            <SuccessBanner
              message={`Scaled to ${(scale.data as { replicas: number } | undefined)?.replicas ?? replicas} replicas — peer list updates within a few seconds.`}
            />
          )}

          {rebalance.isError && (
            <ErrorBanner
              message={`Rebalance failed: ${extractError(rebalance.error) ?? "unknown error"}`}
            />
          )}
          {rebalance.isSuccess && rebalance.data && (
            <SuccessBanner
              message={`Rebalance complete — inspected ${rebalance.data.Inspected}, migrated ${rebalance.data.Migrated}, skipped ${rebalance.data.Skipped}, failures ${rebalance.data.Failures?.length ?? 0}.`}
            />
          )}
          {rebalance.data &&
            rebalance.data.Failures &&
            rebalance.data.Failures.length > 0 && (
              <div className="rounded-md border border-line-1 bg-bg-2/40 p-3 text-2xs">
                <div className="mb-2 font-semibold text-t-2">Migration failures</div>
                <ul className="space-y-1 font-mono text-t-3">
                  {rebalance.data.Failures.map((f) => (
                    <li key={`${f.InstrumentID}-${f.From}-${f.To}`}>
                      {f.InstrumentID}: {f.From} → {f.To} — {f.Err}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </CardBody>
      </Card>

      {/* --- Peers + Ownership --- */}
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Peers</CardTitle>
            <span className="text-2xs text-t-3">
              {sortedPeers.filter((p) => p.healthy).length} routable / {sortedPeers.length}
            </span>
          </CardHeader>
          <CardBody className="p-0">
            {drain.isError && (
              <div className="px-5 py-2">
                <ErrorBanner
                  message={`Drain failed: ${extractError(drain.error) ?? "unknown error"}`}
                />
              </div>
            )}
            {undrain.isError && (
              <div className="px-5 py-2">
                <ErrorBanner
                  message={`Undrain failed: ${extractError(undrain.error) ?? "unknown error"}`}
                />
              </div>
            )}
            {restart.isError && (
              <div className="px-5 py-2">
                <ErrorBanner
                  message={`Restart failed: ${extractError(restart.error) ?? "unknown error"}`}
                />
              </div>
            )}
            <ul className="divide-y divide-line-1">
              {sortedPeers.map((p) => {
                const isSelected = p.peer_id === selectedPeer;
                const recovering = peerIsRecovering(p.peer_id, restartTracking);
                const status = peerStatusBadge(p, recovering);
                return (
                  <li
                    key={p.peer_id}
                    className={`flex items-start justify-between gap-3 px-5 py-3 transition-colors ${
                      isSelected ? "bg-cyan/10" : "hover:bg-bg-2/40"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedPeer(isSelected ? null : p.peer_id)
                      }
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-t-1">{p.peer_id}</span>
                        {/* One status badge per row — the prior shape
                            showed HEALTHY (green) + DRAINING (yellow)
                            side-by-side, which read as "this is fine"
                            even when the peer was held out of the ring
                            and rejecting orders. peerStatusBadge folds
                            health/drain/ring/restart into a single
                            tone-correct label. */}
                        <Badge tone={status.tone}>{status.label}</Badge>
                        {isSelected && <Badge tone="accent">selected</Badge>}
                      </div>
                      <div className="mt-0.5 truncate font-mono text-2xs text-t-3">
                        {p.addr || "—"} · {p.instruments?.length ?? 0} instruments · last_seen{" "}
                        {fmtRelative(p.last_seen)}
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center gap-1">
                      {recovering ? (
                        // During a restart we own the recovery — hide
                        // drain/undrain CTAs so the operator isn't
                        // tempted to fight the automatic flow (e.g.
                        // clicking Undrain on a SIGTERM-induced drain
                        // would race the engine-router's auto-clear).
                        <span className="text-2xs text-t-3 px-2">recovering…</span>
                      ) : p.operator_drained ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={undrain.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            undrain.mutate(p.peer_id);
                          }}
                        >
                          {undrain.isPending && undrain.variables === p.peer_id
                            ? "Undraining…"
                            : "Undrain"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-no hover:bg-no/10"
                          disabled={p.draining || drain.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            drain.mutate(p.peer_id);
                          }}
                        >
                          {drain.isPending && drain.variables === p.peer_id
                            ? "Draining…"
                            : p.draining
                              ? "Draining…"
                              : "Drain"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-t-3 hover:bg-bg-2/40"
                        disabled={restart.isPending || recovering}
                        title={
                          recovering
                            ? "Recovery already in flight — wait for it to finish before bouncing again."
                            : "Delete the peer's pod; StatefulSet recreates it within seconds. Use to recover a wedged peer when undrain alone isn't enough."
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          // Restart is destructive — in-flight commands
                          // the pod was handling are lost, and traffic
                          // re-routes for ~5 s while the new pod boots.
                          // Confirm so an accidental click doesn't bounce
                          // an otherwise-healthy peer.
                          if (
                            !window.confirm(
                              `Restart ${p.peer_id}? The pod will be deleted and recreated by the StatefulSet. Any in-flight commands the pod is handling will be lost.`,
                            )
                          ) {
                            return;
                          }
                          restart.mutate(p.peer_id);
                        }}
                      >
                        {recovering
                          ? "Restarting…"
                          : restart.isPending && restart.variables === p.peer_id
                            ? "Requesting…"
                            : "Restart"}
                      </Button>
                    </div>
                  </li>
                );
              })}
              {sortedPeers.length === 0 && !peers.isLoading && (
                <li className="px-5 py-6">
                  <EmptyState
                    title="No engine peers registered"
                    description="The router has not discovered any matching-engine pods yet. Check that the StatefulSet is scaled up and pods are Ready."
                  />
                </li>
              )}
              {peers.isLoading && (
                <li className="px-5 py-6 text-2xs text-t-3">Loading peers…</li>
              )}
            </ul>
          </CardBody>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>
              {selectedPeer ? `Instruments · ${selectedPeer}` : "Ownership ring"}
            </CardTitle>
            <div className="flex items-center gap-2 text-2xs text-t-3">
              <span>
                {selectedPeer
                  ? `${t("common.counts.instrument", { count: totalInstrumentsForPeer })} · ${t("common.counts.activeLease", { count: activeLeaseCount })}`
                  : t("common.counts.activeLease", { count: instrumentRows.length })}
              </span>
              {selectedPeer && (
                <button
                  type="button"
                  onClick={() => setSelectedPeer(null)}
                  className="rounded border border-line-1 px-2 py-0.5 text-2xs text-t-2 hover:bg-bg-2/40"
                >
                  Clear filter
                </button>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {selectedPeerInfo && (
              <div className="border-b border-line-1 bg-bg-2/40 px-5 py-3 text-2xs">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <span className="text-t-3">Address</span>
                    <div className="truncate font-mono text-t-1">
                      {selectedPeerInfo.addr || "—"}
                    </div>
                  </div>
                  <div>
                    <span className="text-t-3">Routable</span>
                    <div className="text-t-1">
                      {selectedPeerInfo.healthy ? "Yes" : "No"}
                      {selectedPeerInfo.in_ring ? " · in ring" : " · out of ring"}
                    </div>
                  </div>
                  <div>
                    <span className="text-t-3">Drain source</span>
                    <div className="text-t-1">
                      {peerIsRecovering(selectedPeerInfo.peer_id, restartTracking)
                        ? "Engine SIGTERM (restart in progress)"
                        : selectedPeerInfo.operator_drained
                          ? "Operator"
                          : selectedPeerInfo.draining
                            ? "Engine self"
                            : "—"}
                    </div>
                  </div>
                  <div>
                    <span className="text-t-3">Heartbeat instruments</span>
                    <div className="text-t-1">
                      {selectedPeerInfo.instruments?.length ?? 0}
                    </div>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-2">
                    <span className="text-t-3">Last seen</span>
                    <div className="text-t-1">
                      {fmtRelative(selectedPeerInfo.last_seen)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-2xs">
                <thead className="border-b border-line-1 bg-bg-2/40 text-t-3">
                  <tr>
                    <th className="px-5 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Peer</th>
                    <th className="px-3 py-2 text-left">State</th>
                    <th className="px-3 py-2 text-right">
                      {selectedPeer ? "Lease expires" : "Expires"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {instrumentRows.map((r) => (
                    <tr
                      key={r.market_id}
                      className="border-b border-line-1 last:border-0"
                    >
                      <td className="px-5 py-1.5 font-mono">{r.market_id}</td>
                      <td className="px-3 py-1.5 font-mono">
                        <button
                          type="button"
                          onClick={() => setSelectedPeer(r.peer_id)}
                          className="hover:underline"
                        >
                          {r.peer_id}
                        </button>
                        {r.state === "MIGRATING" && r.migrating_to && (
                          <>
                            <span className="text-t-3"> → </span>
                            <button
                              type="button"
                              onClick={() => setSelectedPeer(r.migrating_to!)}
                              className="hover:underline"
                            >
                              {r.migrating_to}
                            </button>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <Badge tone={leaseTone(r.state)}>{r.state}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-right text-t-3">
                        {r.expires_at ? fmtRelative(r.expires_at) : "—"}
                      </td>
                    </tr>
                  ))}
                  {!ownership.isLoading && instrumentRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-6 text-t-3">
                        {selectedPeer
                          ? `No instruments held by ${selectedPeer} — engine has reported no worker state and no leases reference this peer.`
                          : "No ownership leases — engine ring is empty."}
                      </td>
                    </tr>
                  )}
                  {ownership.isLoading && (
                    <tr>
                      <td colSpan={4} className="px-5 py-6 text-t-3">
                        Loading leases…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
